// İSG uzmanı portalı — Mobil Uygulama Bağlantıları (global app-settings; api-contract §26).
// Sistem admini iOS/Android mağaza indirme linklerini tanımlar/temizler. Global singleton
// (firma-bazlı DEĞİL) — token'dan yazılır, form firma seçtirmez.
//
// Yükle `GET /app-settings` (herhangi authenticated okur, §26.1) → doldur → düzenle →
// kaydet `PUT /admin/app-settings` (**IsSystemAdmin**, §26.2). Yazma yalnız sistem adminidir;
// bu sayfa yönetim kabuğu (`AdminGate` — sistem-admin sondası) altındadır. Grant'sız principal
// buraya ULAŞMAZ; ulaşırsa backend `403` döner (nazik hata bildirimi).
//
// URL doğrulama: dolu değer `http`/`https` mutlak adres olmalı; **boş/whitespace** verilen
// alan **temizlenir** (`null` — link kaldırma). Kaydedince cache (`['app-settings']`) güncellenir
// → anasayfa app-info + "Mobil Uygulama" sayfası aynı anda yenilenir. RHF + Zod; Alert bildirim.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Field,
  Input,
  Spinner,
  appSettingsApi,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/** Anasayfa/Mobil Uygulama ile PAYLAŞILAN global app-settings sorgu anahtarı. */
const appSettingsQueryKey = ['app-settings'] as const;

/** Boş/whitespace → `null` (link temizleme); dolu → trim edilmiş değer (backend doğrular). */
function nullableUrl(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

/** `http`/`https` mutlak adres mi (boş değer bu kontrolden ÖNCE elenir). */
function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function AppSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);

  const query = useQuery({
    queryKey: appSettingsQueryKey,
    queryFn: () => appSettingsApi.getAppSettings(),
    staleTime: 5 * 60_000,
  });

  const schema = useMemo(
    () =>
      z.object({
        // Boş = temizle (§26.2); dolu ise http/https mutlak adres olmalı.
        iosAppUrl: z
          .string()
          .trim()
          .refine((v) => v.length === 0 || isHttpUrl(v), t('appSettings.validation.invalidUrl')),
        androidAppUrl: z
          .string()
          .trim()
          .refine((v) => v.length === 0 || isHttpUrl(v), t('appSettings.validation.invalidUrl')),
      }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const settings = query.data;
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      iosAppUrl: settings?.iosAppUrl ?? '',
      androidAppUrl: settings?.androidAppUrl ?? '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      appSettingsApi.updateAppSettings({
        iosAppUrl: nullableUrl(values.iosAppUrl),
        androidAppUrl: nullableUrl(values.androidAppUrl),
      }),
    onSuccess: (updated) => {
      // Paylaşılan cache'i güncelle — anasayfa app-info + "Mobil Uygulama" sayfası da yenilenir.
      queryClient.setQueryData(appSettingsQueryKey, updated);
      setMessage(t('appSettings.updated'));
    },
  });

  return (
    <div className="flex flex-col gap-5">
      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card>
        <CardContent>
          {query.isPending ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Spinner label={t('appSettings.loading')} />
            </div>
          ) : query.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(query.error, t)}</Alert>
          ) : (
            <form
              className="flex max-w-2xl flex-col gap-4"
              onSubmit={handleSubmit((values) => {
                setMessage(null);
                mutation.mutate(values);
              })}
              noValidate
            >
              {mutation.isError ? (
                <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
              ) : null}

              <Field
                htmlFor="app-ios-url"
                label={t('appSettings.field.iosAppUrl')}
                hint={t('appSettings.field.hint')}
                error={formState.errors.iosAppUrl?.message}
              >
                <Input
                  id="app-ios-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://apps.apple.com/..."
                  invalid={Boolean(formState.errors.iosAppUrl)}
                  {...register('iosAppUrl')}
                />
              </Field>

              <Field
                htmlFor="app-android-url"
                label={t('appSettings.field.androidAppUrl')}
                hint={t('appSettings.field.hint')}
                error={formState.errors.androidAppUrl?.message}
              >
                <Input
                  id="app-android-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://play.google.com/store/apps/details?id=..."
                  invalid={Boolean(formState.errors.androidAppUrl)}
                  {...register('androidAppUrl')}
                />
              </Field>

              <div className="flex items-center justify-end pt-1">
                <Button type="submit" loading={mutation.isPending}>
                  {mutation.isPending ? (
                    <Spinner label={t('appSettings.save')} />
                  ) : (
                    t('appSettings.save')
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
